import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getEntry } from "core-integration/src/store/entries/reducers";
import { fetchEntrySummary } from "core-integration/src/store/entries/thunks";
import { getTotalPlayers } from "core-integration/src/store/game/reducers";
import { fetchMyTeam } from "core-integration/src/store/my-team/thunks";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { getTransferState } from "core-integration/src/store/squad/reducers";
import { integerToMoney } from "core-integration/src/utils/money";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink } from "react-router";
import Button from "../Button";
import { FirebaseEntryLinks } from "../FirebaseEntryLinks";
import Flag from "../Flag";
import Footnote from "../Footnote";
import { ControlArrowRight } from "../icons/Arrows";
import ManageYourLeagues from "../leagues/admin/ManageYourLeagues";
import LogoBar from "../LogoBar";
import Panel from "../Panel";
import Table from "../Table";
import {
  BoldText,
  ButtonLayoutWrapper,
  DataListHeading,
  DataListItem,
  DataListValue,
  EntryFullName,
  EntryName,
  EntryNameWrapper,
  FooterWrapper,
  PanelWrapper,
  StyledLink,
  StyledTdFixedWidth,
} from "./styles";
import { EntryProps } from "./types";

const Entry = ({ entryId }: EntryProps) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const { t } = useTranslation();
  const player = useSelector(getPlayerData);
  const entry = useSelector((state: RootState) => getEntry(state, entryId));
  const mine = Boolean(player && player.entry && player.entry === entryId);
  const transfersState = useSelector((state: RootState) =>
    getTransferState(state),
  );
  const totalPlayers = useSelector((state: RootState) =>
    getTotalPlayers(state),
  );

  useEffect(() => {
    if (mine && !transfersState) {
      dispatch(fetchMyTeam());
    }
    if (!entry) {
      dispatch(fetchEntrySummary(entryId));
    }
  }, []);

  if (!entry) {
    return null;
  }

  return (
    <div>
      <EntryNameWrapper>
        <EntryFullName>
          {entry.player_first_name} {entry.player_last_name}
        </EntryFullName>

        <LogoBar justifyContent="space-between">
          <EntryName>{entry.name}</EntryName>
          <Flag code={entry.player_region_iso_code_short} />
        </LogoBar>
      </EntryNameWrapper>

      <PanelWrapper>
        <Panel>
          <Panel.Header title={t("myTeam.pointsRankings", "Points/Rankings")} />
          <Panel.Body>
            <Table size="lg">
              <tbody>
                <tr>
                  <td>{t("myTeam.overallPoints", "Overall Points")}:</td>
                  <td>
                    <BoldText>{entry.summary_overall_points} </BoldText>
                  </td>
                </tr>
                <tr>
                  <td>{t("myTeam.overallRank", "Overall Rank")}:</td>
                  <td>
                    <BoldText>
                      {entry.summary_overall_rank &&
                        entry.summary_overall_rank.toLocaleString()}
                    </BoldText>
                  </td>
                </tr>
                <tr>
                  <td>{t("myTeam.totalPlayers", "Total Players")}:</td>
                  <td>
                    <BoldText>{totalPlayers.toLocaleString()}</BoldText>
                  </td>
                </tr>
                <tr>
                  <td>{t("myTeam.roundPoints", "Round Points")}:</td>
                  <td>
                    <BoldText>{entry.summary_event_points}</BoldText>
                  </td>
                </tr>
              </tbody>
            </Table>
          </Panel.Body>
          <Panel.Footer>
            <StyledLink to={`/entry/${entry.id}/history`}>
              {t("myTeam.viewRoundHistory", "View Round history")}
            </StyledLink>
          </Panel.Footer>
        </Panel>
      </PanelWrapper>
      <PanelWrapper>
        <ManageYourLeagues entryId={entryId} />
      </PanelWrapper>
      {mine && transfersState && (
        <PanelWrapper>
          <Panel>
            <StyledLink to={`/entry/${entry.id}/transfers`}>
              <Panel.Header title={t("myTeam.trans.transfersN", "Transfers")} />
            </StyledLink>
            <Panel.Body>
              <Table size="lg">
                <tbody>
                  <tr>
                    <td>{t("myTeam.roundTransfers", "Round transfers")}:</td>
                    <StyledTdFixedWidth>
                      <BoldText>{transfersState.made}</BoldText>
                    </StyledTdFixedWidth>
                  </tr>
                  <tr>
                    <td>{t("myTeam.totalTransfers", "Total transfers")}:</td>
                    <StyledTdFixedWidth>
                      <BoldText>
                        {entry.last_deadline_total_transfers +
                          transfersState.made}
                      </BoldText>
                    </StyledTdFixedWidth>
                  </tr>
                </tbody>
              </Table>
            </Panel.Body>
            <Panel.Header title={t("myTeam.trans.financesN", "Finances")} />

            <Panel.Body>
              <Table size="lg">
                <tbody>
                  <tr>
                    <td>{t("myTeam.squadValue", "Squad value")}:</td>
                    <StyledTdFixedWidth>
                      <BoldText>
                        {integerToMoney(transfersState.value, 10)}
                      </BoldText>
                    </StyledTdFixedWidth>
                  </tr>
                  <tr>
                    <td>{t("myTeam.inTheBank", "In the bank")}:</td>
                    <StyledTdFixedWidth>
                      <BoldText>
                        {integerToMoney(transfersState.bank, 10)}
                      </BoldText>
                    </StyledTdFixedWidth>
                  </tr>
                </tbody>
              </Table>
            </Panel.Body>
          </Panel>
        </PanelWrapper>
      )}
      {!mine && (
        <PanelWrapper>
          <Panel>
            <Panel.Header
              title={
                <>
                  {t("myTeam.transfersAndFinance", "Transfers and Finance")}{" "}
                  <sup>*</sup>
                </>
              }
            />
            <Panel.Body>
              <p />
              <ul>
                <DataListItem>
                  <DataListHeading>
                    {t("myTeam.totalTransfers", "Total transfers")}:
                  </DataListHeading>
                  <DataListValue>
                    {entry.last_deadline_total_transfers}
                  </DataListValue>
                </DataListItem>
              </ul>
            </Panel.Body>
            <Panel.Footer>
              <StyledLink to={`/entry/${entry.id}/transfers`}>
                {t("myTeam.viewTransferHistory", "View transfer history")}
              </StyledLink>
            </Panel.Footer>
            <Panel.Body>
              <ul>
                <DataListItem>
                  <DataListHeading>
                    {t("myTeam.squadValue", "Squad value")}:
                  </DataListHeading>
                  <DataListValue>
                    {entry.last_deadline_value !== null &&
                    entry.last_deadline_bank !== null
                      ? `M SAR${integerToMoney(
                          entry.last_deadline_value - entry.last_deadline_bank,
                          10,
                        )}`
                      : "-"}
                  </DataListValue>
                </DataListItem>
                <DataListItem>
                  <DataListHeading>
                    {t("myTeam.inTheBank", "In the bank")}:
                  </DataListHeading>
                  <DataListValue>
                    {entry.last_deadline_bank !== null
                      ? `M SAR${integerToMoney(entry.last_deadline_bank, 10)}`
                      : "-"}
                  </DataListValue>
                </DataListItem>
              </ul>
              <FooterWrapper>
                <Footnote>* Values at the last deadline</Footnote>
              </FooterWrapper>
            </Panel.Body>
          </Panel>
        </PanelWrapper>
      )}
      {mine && (
        <PanelWrapper>
          <Panel>
            <Panel.Header title={t("myTeam.admin", "Admin")} />
            <Panel.Body>
              {mine && (
                <ButtonLayoutWrapper>
                  <ButtonLayoutWrapper>
                    <Button
                      width={1}
                      variant="primary"
                      as={RouterLink}
                      to={`/entry-update`}
                      endIcon={<ControlArrowRight color={"primary"} />}
                    >
                      {t("teamDetails", "Team details")}
                    </Button>
                  </ButtonLayoutWrapper>
                  <FirebaseEntryLinks />
                </ButtonLayoutWrapper>
              )}
            </Panel.Body>
          </Panel>
        </PanelWrapper>
      )}
    </div>
  );
};

export default Entry;
